import type { PermitActions } from '@contracts/permit/PermitActions';
import type { PermitTypeUnit } from '@contracts/permitType/PermitType';
import type { Timestamp } from '@src/common/time/Timestamp';
import { FormFields } from '@src/orders/domain/CheckedFields';
import { accumulatePermitToForm } from '@src/orders/domain/Order';
import type {
  Document,
  EntryValue,
} from '@src/orders/domain/PermitApplication';
import { PermitStatus } from '@src/orders/domain/PermitApplication';
import type { DuplicationTrigger } from '@src/orders/hooks/useDuplicatePermitAsNew';
import { permitsFormValidations } from '@src/orders/schemas/EditOrderFormInputs';

interface PermitSummaryAttributes {
  id: string;
  status: PermitStatus;
  type: string;
  jurisdiction: string;
  jurisdictionUrl?: string;
  unit: PermitTypeUnit;
  stateId?: string;
  effectiveDate: Timestamp;
  claimedBy?: string;
  statePermitNumber?: string;
  statePermitDocument?: Document;
  trigger?: DuplicationTrigger;
  isActive: boolean;
  isPriority: boolean;
  isSelfIssued: boolean;
  hasPendingChanges: boolean;
  keptAsIs: boolean;
  fields: string[];
  entries: Record<string, EntryValue>;
  allowedActions: PermitActions[];
}

export class PermitSummary {
  readonly id: string;
  readonly status: PermitStatus;
  readonly type: string;
  readonly jurisdiction: string;
  readonly jurisdictionUrl?: string;
  readonly unit: PermitTypeUnit;
  readonly stateId?: string;
  readonly effectiveDate: Timestamp;
  readonly claimedBy?: string;
  readonly trigger?: DuplicationTrigger;
  readonly statePermitNumber?: string;
  readonly isActive: boolean;
  readonly isPriority: boolean;
  readonly isSelfIssued: boolean;
  readonly statePermitDocument?: Document;
  readonly hasPendingChanges: boolean;
  readonly keptAsIs: boolean;
  readonly fields: string[];
  readonly entries: Record<string, EntryValue>;
  readonly allowedActions: PermitActions[];

  constructor(attributes: PermitSummaryAttributes) {
    this.id = attributes.id;
    this.status = attributes.status;
    this.type = attributes.type;
    this.jurisdiction = attributes.jurisdiction;
    this.jurisdictionUrl = attributes.jurisdictionUrl;
    this.unit = attributes.unit;
    this.stateId = attributes.stateId;
    this.effectiveDate = attributes.effectiveDate;
    this.claimedBy = attributes.claimedBy;
    this.trigger = attributes.trigger;
    this.statePermitNumber = attributes.statePermitNumber;
    this.isActive = attributes.isActive;
    this.isPriority = attributes.isPriority;
    this.isSelfIssued = attributes.isSelfIssued;
    this.statePermitDocument = attributes.statePermitDocument;
    this.hasPendingChanges = attributes.hasPendingChanges;
    this.keptAsIs = attributes.keptAsIs;
    this.fields = attributes.fields;
    this.entries = attributes.entries;
    this.allowedActions = attributes.allowedActions;
  }

  hasFilledAllRequiredFields() {
    const permitForm = accumulatePermitToForm({
      form: {},
      permit: {
        id: this.id,
        entries: this.entries,
        effectiveDate: this.effectiveDate,
      },
    });
    try {
      const convertedFields = FormFields.convertToFormFields({
        checkedFields: this.fields,
      });
      permitsFormValidations({
        permitCheckedFields: [{ id: this.id, fields: convertedFields.fields }],
        checkedFields: convertedFields.fields,
      }).validateSync(permitForm);

      return true;
    } catch (_error) {
      return false;
    }
  }

  canDo(action: PermitActions): boolean {
    return this.allowedActions.includes(action);
  }

  canModifyAssignee(clientIsUser: boolean): boolean {
    return !clientIsUser && this.status === PermitStatus.NEW;
  }
}
